import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { SparePartService } from '../../../../services/spare-part.service';
import { TranslationService } from '../../../../services/translation';
import { ToastActions } from '../../../toast/toast.actions';
import * as InventoryCorrectionActions from './inventory-correction.actions';
import { selectInventoryCorrectionLastListRequest } from './inventory-correction.selectors';

@Injectable()
export class InventoryCorrectionEffects {
  private actions$ = inject(Actions);
  private sparePartService = inject(SparePartService);
  private store = inject(Store);
  private translationService = inject(TranslationService);

  loadInventoryCorrectionsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InventoryCorrectionActions.loadInventoryCorrections),
      switchMap(({ params }) =>
        this.sparePartService.getAllInventoryCorrections(params).pipe(
          map((res) => {
            const body = res.body;
            const data = body?.data ?? [];
            const meta = body?.page;
            return InventoryCorrectionActions.loadInventoryCorrectionsSuccess({
              data,
              totalElements: meta?.totalElements ?? data.length,
              page: meta?.page ?? params.page ?? 0,
              size: meta?.size ?? params.size ?? 10,
              totalPages: meta?.totalPages ?? 1,
            });
          }),
          catchError((error) =>
            of(InventoryCorrectionActions.loadInventoryCorrectionsFailure({ error }))
          )
        )
      )
    )
  );

  loadInventoryCorrectionsAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InventoryCorrectionActions.loadInventoryCorrectionsAll),
      switchMap(() =>
        this.sparePartService
          .getAllInventoryCorrections({ page: 0, size: 250, sort: 'createdAt,desc' })
          .pipe(
            switchMap((res) => {
              const body = res.body;
              const firstChunk = body?.data ?? [];
              const totalPages = Math.max(1, body?.page?.totalPages ?? 1);
              if (totalPages <= 1) {
                return of(
                  InventoryCorrectionActions.loadInventoryCorrectionsAllSuccess({ data: firstChunk })
                );
              }
              const rest$ = Array.from({ length: totalPages - 1 }, (_, i) =>
                this.sparePartService
                  .getAllInventoryCorrections({ page: i + 1, size: 250, sort: 'createdAt,desc' })
                  .pipe(
                    take(1),
                    map((r) => r.body?.data ?? [])
                  )
              );
              return forkJoin(rest$).pipe(
                map((chunks) => {
                  const merged = firstChunk.slice();
                  for (const c of chunks) {
                    merged.push(...c);
                  }
                  return InventoryCorrectionActions.loadInventoryCorrectionsAllSuccess({
                    data: merged,
                  });
                })
              );
            }),
            catchError((error) =>
              of(InventoryCorrectionActions.loadInventoryCorrectionsAllFailure({ error }))
            )
          )
      )
    )
  );

  loadListErrorToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        InventoryCorrectionActions.loadInventoryCorrectionsFailure,
        InventoryCorrectionActions.loadInventoryCorrectionsAllFailure
      ),
      map(() =>
        ToastActions.showErrorToast({
          detail: this.translationService.translate('inventory.stockUpdateRequests.loadError'),
        })
      )
    )
  );

  reviewInventoryCorrection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InventoryCorrectionActions.reviewInventoryCorrection),
      switchMap(({ id, body }) =>
        this.sparePartService.reviewInventoryCorrection(id, body).pipe(
          mergeMap((res) => {
            const payload = res.body;
            if (payload?.status === false) {
              return of(
                InventoryCorrectionActions.reviewInventoryCorrectionBusinessFailure({
                  message:
                    payload.message?.trim() ||
                    this.translationService.translate('inventory.stockUpdateRequests.reviewError'),
                })
              );
            }
            const detail =
              payload?.message?.trim() ||
              this.translationService.translate('inventory.stockUpdateRequests.toastApproved');
            return from([
              InventoryCorrectionActions.reviewInventoryCorrectionSuccess({ response: payload! }),
              ToastActions.showSuccessToast({
                summary: this.translationService.translate('common.success'),
                detail,
              }),
            ]);
          }),
          catchError((error) =>
            of(InventoryCorrectionActions.reviewInventoryCorrectionFailure({ error }))
          )
        )
      )
    )
  );

  reloadListAfterReviewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InventoryCorrectionActions.reviewInventoryCorrectionSuccess),
      switchMap(() =>
        this.store.select(selectInventoryCorrectionLastListRequest).pipe(
          take(1),
          mergeMap((last) => {
            if (!last) {
              return EMPTY;
            }
            if (last.kind === 'all') {
              return of(InventoryCorrectionActions.loadInventoryCorrectionsAll());
            }
            return of(
              InventoryCorrectionActions.loadInventoryCorrections({
                params: { ...last.params, page: 0 },
              })
            );
          })
        )
      )
    )
  );
}
